import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Mail, Lock, X, ArrowRight, Phone, User, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';
import { toast } from 'react-hot-toast';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type AuthMode = 'signin' | 'signup' | 'verify';
type SignInMethod = 'phone' | 'email';

interface FormInputs {
  phone?: string;
  email?: string;
  password?: string;
  otp?: string;
  name?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [signInMethod, setSignInMethod] = useState<SignInMethod>('phone');
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormInputs>();
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    try {
      if (signInMethod === 'phone') {
        if (!showOTP) {
          // Simulate phone verification
          if (data.phone === '1234567890') {
            setShowOTP(true);
            toast.success('OTP sent to your phone');
          } else {
            throw new Error('Invalid phone number');
          }
        } else {
          // Verify OTP
          if (data.otp === '123456') {
            toast.success('Successfully signed in');
            if (onSuccess) onSuccess();
            onClose();
          } else {
            throw new Error('Invalid OTP');
          }
        }
      } else {
        // Email sign in/up logic
        if (mode === 'signup') {
          // Simulate email signup
          toast.success('Account created successfully');
        } else {
          // Simulate email signin
          toast.success('Signed in successfully');
        }
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  };

  const switchSignInMethod = () => {
    setSignInMethod(signInMethod === 'phone' ? 'email' : 'phone');
    setShowOTP(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            {/* Modal */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
              className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 sm:p-8"
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {mode === 'signup' ? 'Create Account' : 'Welcome Back'}
                </h2>
                <p className="mt-2 text-gray-600">
                  {mode === 'signup' ? 'Create a new account' : 'Sign in to your account'}
                </p>
              </div>

              {/* Sign In Method Toggle */}
              <div className="mb-6">
                <div className="flex justify-center gap-4">
                  <Button
                    variant={signInMethod === 'phone' ? 'primary' : 'outline'}
                    onClick={() => setSignInMethod('phone')}
                    icon={<Phone className="h-4 w-4" />}
                  >
                    Phone
                  </Button>
                  <Button
                    variant={signInMethod === 'email' ? 'primary' : 'outline'}
                    onClick={() => setSignInMethod('email')}
                    icon={<Mail className="h-4 w-4" />}
                  >
                    Email
                  </Button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {mode === 'signup' && !showOTP && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="text"
                        {...register('name', { required: mode === 'signup' })}
                        className="input pl-10"
                        placeholder="Enter your full name"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">Name is required</p>
                    )}
                  </div>
                )}

                {signInMethod === 'phone' ? (
                  !showOTP ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="tel"
                          defaultValue="1234567890"
                          {...register('phone', { 
                            required: true,
                            pattern: /^[0-9]{10}$/
                          })}
                          className="input pl-10"
                          placeholder="1234567890"
                        />
                      </div>
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">Valid phone number is required</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enter OTP
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="text"
                          defaultValue="123456"
                          {...register('otp', { 
                            required: true,
                            pattern: /^[0-9]{6}$/
                          })}
                          className="input pl-10"
                          placeholder="Enter 6-digit OTP"
                        />
                      </div>
                      {errors.otp && (
                        <p className="mt-1 text-sm text-red-600">Valid OTP is required</p>
                      )}
                    </div>
                  )
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="email"
                          {...register('email', { 
                            required: true,
                            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                          })}
                          className="input pl-10"
                          placeholder="Enter your email"
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">Valid email is required</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="password"
                          {...register('password', { required: true, minLength: 6 })}
                          className="input pl-10"
                          placeholder="Enter your password"
                        />
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">Password must be at least 6 characters</p>
                      )}
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  isLoading={loading}
                  icon={<ArrowRight className="h-4 w-4" />}
                  iconPosition="right"
                >
                  {signInMethod === 'phone' 
                    ? (!showOTP ? 'Get OTP' : 'Verify OTP')
                    : (mode === 'signup' ? 'Sign Up' : 'Sign In')
                  }
                </Button>

                {/* Mode Switch */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;